// Channel State - NGXS State Management
import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import { tap, catchError, expand, reduce } from 'rxjs/operators';
import { EMPTY, of } from 'rxjs';
import { Channel, ChannelConnection, ChannelFilter, ChannelStats, InternalNote } from '../../models/chat.models';
import { ChatService } from '../../services/chat.service';

// Import individual actions using ES2015 syntax
import {
    LoadChannels,
    LoadChannelsSuccess,
    LoadChannelsError,
    LoadAllChannels,
    LoadAllChannelsSuccess,
    LoadAllChannelsError,
    AddChannel,
    UpdateChannel,
    RemoveChannel,
    CreateChannel,
    JoinChannel,
    ClearChannels,
    LoadChannelById,
    LoadChannelByIdSuccess,
    LoadChannelByIdError,
    UpdateOneChannel,
    SetSelectedChannel,
    AssignAgent,
    LoadChannelStats,
    LoadChannelStatsSuccess,
    LoadGlobalChannelStats,
    LoadGlobalChannelStatsSuccess,
    LoadChannelsByType,
    LoadChannelsByTypeSuccess,
    ClearFilteredChannels,
    SetChannelSort,
    AddInternalNote,
    DeleteInternalNote,
} from '../actions/channel.actions';

export interface ChannelStateModel {
    channels: Channel[];
    allChannels: Channel[];
    filteredChannels: Channel[];
    filteredNextToken?: string;
    loading: boolean;
    error: unknown;
    lastUpdated: number;
    nextToken?: string;
    selectedChannelId?: string;
    stats?: ChannelStats;
    globalStats?: ChannelStats;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    currentFilter?: ChannelFilter;
}

    @State<ChannelStateModel>({
    name: 'channels',
    defaults: {
        channels: [],
        allChannels: [],
        loading: false,
        error: null,
        lastUpdated: 0,
        filteredChannels: [],
        filteredNextToken: undefined,
        nextToken: undefined,
        selectedChannelId: undefined,
        stats: undefined,
        globalStats: undefined,
        sortBy: 'updatedAt',
        sortOrder: 'desc',
        currentFilter: undefined,
    },
})
@Injectable()
export class ChannelState {
    constructor(private chatService: ChatService) {}

    @Selector()
    static getChannels(state: ChannelStateModel): Channel[] {
        return state.channels;
    }

    @Selector()
    static getChannelById(state: ChannelStateModel) {
        return (channelId: string): Channel | undefined => {
            return state.channels.find((channel) => channel._id === channelId);
        };
    }

    @Selector()
    static getChannelsByUserId(state: ChannelStateModel) {
        return (userId: string): Channel[] => {
            return state.channels.filter((channel) => channel.members.includes(userId));
        };
    }

    @Selector()
    static getChannelsByType(state: ChannelStateModel) {
        return (types: string[]): Channel[] => {
            return state.channels.filter((channel) => types.includes(channel.type));
        };
    }

    @Selector()
    static isLoading(state: ChannelStateModel): boolean {
        return state.loading;
    }

    @Selector()
    static getError(state: ChannelStateModel): unknown {
        return state.error;
    }

    @Selector()
    static getLastUpdated(state: ChannelStateModel): number {
        return state.lastUpdated;
    }

    @Selector()
    static getAllChannels(state: ChannelStateModel): Channel[] {
        return state.allChannels;
    }

    @Selector()
    static getNextToken(state: ChannelStateModel): string | undefined {
        return state.nextToken;
    }

    @Selector()
    static getSelectedChannelId(state: ChannelStateModel): string | undefined {
        return state.selectedChannelId;
    }

    @Selector()
    static getStats(state: ChannelStateModel): ChannelStats | undefined {
        return state.stats;
    }

    @Selector()
    static getGlobalStats(state: ChannelStateModel): ChannelStats | undefined {
        return state.globalStats;
    }

    @Selector()
    static getFilteredChannels(state: ChannelStateModel): Channel[] {
        return state.filteredChannels;
    }

    @Selector()
    static getFilteredNextToken(state: ChannelStateModel): string | undefined {
        return state.filteredNextToken;
    }

    @Selector()
    static getSelectedChannel(state: ChannelStateModel): Channel | undefined {
        if (!state.selectedChannelId) return undefined;
        // Prefer in-memory channels list, fall back to allChannels if needed
        return (
            state.channels.find((c) => c._id === state.selectedChannelId) ||
            state.allChannels.find((c) => c._id === state.selectedChannelId)
        );
    }

    @Action(LoadChannels)
    loadChannels(ctx: StateContext<ChannelStateModel>, action: LoadChannels) {
        ctx.patchState({ loading: true, error: null });

        return this.chatService.getChannels(action.userId).pipe(
            tap((channels) => {
                ctx.dispatch(new LoadChannelsSuccess(channels));
            }),
            catchError((error) => {
                ctx.dispatch(new LoadChannelsError(error));
                return of(error);
            }),
        );
    }

    @Action(LoadChannelsSuccess)
    loadChannelsSuccess(ctx: StateContext<ChannelStateModel>, action: LoadChannelsSuccess) {
        const state = ctx.getState();
        const existingMap = new Map(state.channels.map((c) => [c._id, c]));
        const merged = action.channels.map((incoming) => {
            const existing = existingMap.get(incoming._id);
            if (!existing) return incoming;
            return {
                ...incoming,
                lastSenderId: incoming.lastSenderId ?? existing.lastSenderId,
            };
        });
        ctx.patchState({
            channels: merged,
            loading: false,
            error: null,
            lastUpdated: Date.now(),
        });
    }

    @Action(LoadChannelsError)
    loadChannelsError(ctx: StateContext<ChannelStateModel>, action: LoadChannelsError) {
        ctx.patchState({
            loading: false,
            error: action.error,
        });
    }

    @Action(LoadAllChannels, { cancelUncompleted: true })
    loadAllChannels(ctx: StateContext<ChannelStateModel>, action: LoadAllChannels) {
        const isFreshLoad = !action.nextToken;
        ctx.patchState({
            loading: true,
            error: null,
            ...(isFreshLoad ? { allChannels: [], nextToken: undefined } : {}),
        });

        const state = ctx.getState();
        const sortBy = action.sortBy ?? state.sortBy;
        const sortOrder = action.sortOrder ?? state.sortOrder;

        const isPaginating = !!action.nextToken;
        const filter = isPaginating
            ? (action.filter !== undefined ? action.filter : state.currentFilter)
            : action.filter;

        if (!isPaginating) {
            ctx.patchState({ currentFilter: filter });
        } else if (action.filter !== undefined) {
            ctx.patchState({ currentFilter: action.filter });
        }

        const wantedLimit = action.limit ?? 50;
        const baseCount = isPaginating ? ctx.getState().allChannels.length : 0;
        let accumulated = 0;

        // Use expand() to accumulate all pages in memory and emit a single
        // state update instead of the previous waterfall of dispatches that
        // caused intermediate renders on every page.
        return this.chatService.getAllChannels(action.limit, action.nextToken, sortBy, sortOrder, filter).pipe(
            expand((conn) => {
                accumulated += conn.items.length;
                if (conn.nextToken && (baseCount + accumulated) < wantedLimit) {
                    return this.chatService.getAllChannels(action.limit, conn.nextToken, sortBy, sortOrder, filter);
                }
                return EMPTY;
            }),
            reduce<ChannelConnection, { items: Channel[]; nextToken?: string }>((acc, conn) => ({
                items: [...acc.items, ...conn.items],
                nextToken: conn.nextToken,
            }), { items: [], nextToken: undefined }),
            tap((merged) => {
                ctx.dispatch(new LoadAllChannelsSuccess(merged, action.nextToken));
                ctx.patchState({ loading: false });
            }),
            catchError((error) => {
                ctx.dispatch(new LoadAllChannelsError(error));
                return of(error);
            }),
        );
    }

    @Action(SetChannelSort, { cancelUncompleted: true })
    setChannelSort(ctx: StateContext<ChannelStateModel>, action: SetChannelSort) {
        const state = ctx.getState();
        // Use explicitly passed filter, otherwise fall back to last stored filter
        const filter = action.filter !== undefined ? action.filter : state.currentFilter;
        ctx.patchState({
            sortBy: action.sortBy,
            sortOrder: action.sortOrder,
            allChannels: [],
            nextToken: undefined,
            currentFilter: filter,
        });
        return ctx.dispatch(new LoadAllChannels(action.limit, undefined, action.sortBy, action.sortOrder, filter));
    }

    @Action(LoadAllChannelsSuccess)
    loadAllChannelsSuccess(ctx: StateContext<ChannelStateModel>, action: LoadAllChannelsSuccess) {
        const state = ctx.getState();
        const shouldAppend = !!action.requestNextToken;
        const existingChannels = shouldAppend ? state.allChannels : [];
        const existingMap = new Map(state.allChannels.map((c) => [c._id, c]));

        // Merge incoming items: preserve locally-set fields (e.g. lastSenderId) that
        // DynamoDB may not have returned yet.
        const merged = action.channelConnection.items.map((incoming) => {
            const existing = existingMap.get(incoming._id);
            if (!existing) return incoming;
            return {
                ...incoming,
                lastSenderId: incoming.lastSenderId ?? existing.lastSenderId,
            };
        });

        const existingIds = new Set(existingChannels.map((c) => c._id));
        const newItems = merged.filter((c) => !existingIds.has(c._id));
        const updatedChannels = [...existingChannels, ...newItems];

        // Don't touch `loading` here — the loadAllChannels handler manages
        // the loading lifecycle to avoid flicker during auto-pagination.
        ctx.patchState({
            allChannels: updatedChannels,
            nextToken: action.channelConnection.nextToken,
            error: null,
            lastUpdated: Date.now(),
        });
    }

    @Action(LoadAllChannelsError)
    loadAllChannelsError(ctx: StateContext<ChannelStateModel>, action: LoadAllChannelsError) {
        ctx.patchState({
            loading: false,
            error: action.error,
        });
    }

    @Action(AddChannel)
    addChannel(ctx: StateContext<ChannelStateModel>, action: AddChannel) {
        const state = ctx.getState();
        const existingIndex = state.channels.findIndex((c) => c._id === action.channel._id);

        if (existingIndex === -1) {
            const existingAllIds = new Set(state.allChannels.map((c) => c._id));
            const newAllChannels = existingAllIds.has(action.channel._id)
                ? state.allChannels
                : [...state.allChannels, action.channel];

            ctx.patchState({
                channels: [...state.channels, action.channel],
                allChannels: newAllChannels,
                lastUpdated: Date.now(),
            });
        }
    }

    @Action(UpdateChannel)
    updateChannel(ctx: StateContext<ChannelStateModel>, action: UpdateChannel) {
        const state = ctx.getState();
        const id = action.channel._id;
        const merge = (list: Channel[]) =>
            list.map((channel) =>
                channel._id === id ? { ...channel, ...action.channel } : channel,
            );

        ctx.patchState({
            channels: merge(state.channels),
            allChannels: merge(state.allChannels),
            filteredChannels: merge(state.filteredChannels),
            lastUpdated: Date.now(),
        });
    }

    @Action(UpdateOneChannel)
    updateOneChannel(ctx: StateContext<ChannelStateModel>, action: UpdateOneChannel) {
        ctx.patchState({ loading: true, error: null });
        // Persist to backend first, then update local state with server truth
        const { channelId, name, status, description, senderAvatar, priority } = action;

        return this.chatService
            .updateChannel({
                channelId,
                name,
                status,
                priority,
                description,
                senderAvatar,
            })
            .pipe(
                tap((updated) => {
                    ctx.dispatch(new UpdateChannel(updated));
                    ctx.patchState({ loading: false });
                }),
                catchError((error) => {
                    ctx.patchState({ loading: false, error });
                    return of(error);
                }),
            );
    }

    @Action(AddInternalNote)
    addInternalNote(ctx: StateContext<ChannelStateModel>, action: AddInternalNote) {
        const state = ctx.getState();
        const channel =
            state.allChannels.find((c) => c._id === action.channelId) ||
            state.channels.find((c) => c._id === action.channelId);
        if (!channel) return of(null);

        const existing: InternalNote[] = (() => {
            try {
                return JSON.parse(channel.internalNotes ?? '[]');
            } catch {
                return [];
            }
        })();
        const updated = [action.note, ...existing];

        return this.chatService
            .updateChannel({ channelId: action.channelId, internalNotes: JSON.stringify(updated) })
            .pipe(
                tap((updatedChannel) => ctx.dispatch(new UpdateChannel(updatedChannel))),
                catchError((error) => {
                    console.error('Error adding internal note:', error);
                    return of(error);
                }),
            );
    }

    @Action(DeleteInternalNote)
    deleteInternalNote(ctx: StateContext<ChannelStateModel>, action: DeleteInternalNote) {
        const state = ctx.getState();
        const channel =
            state.allChannels.find((c) => c._id === action.channelId) ||
            state.channels.find((c) => c._id === action.channelId);
        if (!channel) return of(null);

        const existing: InternalNote[] = (() => {
            try {
                return JSON.parse(channel.internalNotes ?? '[]');
            } catch {
                return [];
            }
        })();
        const updated = existing.filter((n) => n.id !== action.noteId);

        return this.chatService
            .updateChannel({ channelId: action.channelId, internalNotes: JSON.stringify(updated) })
            .pipe(
                tap((updatedChannel) => ctx.dispatch(new UpdateChannel(updatedChannel))),
                catchError((error) => {
                    console.error('Error deleting internal note:', error);
                    return of(error);
                }),
            );
    }

    @Action(RemoveChannel)
    removeChannel(ctx: StateContext<ChannelStateModel>, action: RemoveChannel) {
        const state = ctx.getState();
        const channels = state.channels.filter((channel) => channel._id !== action.channelId);
        const selectedChannelId =
            state.selectedChannelId === action.channelId ? undefined : state.selectedChannelId;

        ctx.patchState({
            channels,
            selectedChannelId,
            lastUpdated: Date.now(),
        });
    }

    @Action(CreateChannel)
    createChannel(ctx: StateContext<ChannelStateModel>, action: CreateChannel) {
        ctx.patchState({ loading: true, error: null });

        return this.chatService
            .createChannel(
                action.name,
                action.description,
                action.type,
                action.members,
                action.referenceNo,
                action.iconName,
            )
            .pipe(
                tap((channel) => {
                    ctx.dispatch(new AddChannel(channel));
                    ctx.patchState({ loading: false });
                }),
                catchError((error) => {
                    ctx.patchState({ loading: false, error });
                    return of(error);
                }),
            );
    }

    @Action(JoinChannel)
    joinChannel(ctx: StateContext<ChannelStateModel>, action: JoinChannel) {
        return this.chatService.joinChannel(action.channelId).pipe(
            tap((channel) => {
                ctx.dispatch(new UpdateChannel(channel));
            }),
            catchError((error) => {
                ctx.patchState({ error });
                return of(error);
            }),
        );
    }

    @Action(ClearChannels)
    clearChannels(ctx: StateContext<ChannelStateModel>) {
        ctx.patchState({
            channels: [],
            allChannels: [],
            loading: false,
            error: null,
            lastUpdated: 0,
            nextToken: undefined,
            selectedChannelId: undefined,
        });
    }

    @Action(LoadChannelById)
    loadChannelById(ctx: StateContext<ChannelStateModel>, action: LoadChannelById) {
        ctx.patchState({ loading: true, error: null });
        return this.chatService.getChannel(action.channelId).pipe(
            tap((channel) => channel && ctx.dispatch(new LoadChannelByIdSuccess(channel))),
            catchError((error) => ctx.dispatch(new LoadChannelByIdError(error))),
        );
    }

    @Action(LoadChannelByIdSuccess)
    loadChannelByIdSuccess(ctx: StateContext<ChannelStateModel>, action: LoadChannelByIdSuccess) {
        const state = ctx.getState();

        // upsert into channels
        const channels = [...state.channels];
        const idx = channels.findIndex((c) => c._id === action.channel._id);
        if (idx === -1) {
            channels.push(action.channel);
        } else {
            channels[idx] = { ...channels[idx], ...action.channel };
        }

        // also upsert into allChannels for consistency with your list view
        const allChannels = [...state.allChannels];
        const allIdx = allChannels.findIndex((c) => c._id === action.channel._id);
        if (allIdx === -1) {
            allChannels.push(action.channel);
        } else {
            allChannels[allIdx] = { ...allChannels[allIdx], ...action.channel };
        }

        ctx.patchState({
            channels,
            allChannels,
            loading: false,
            error: null,
            lastUpdated: Date.now(),
        });
    }

    @Action(LoadChannelByIdError)
    loadChannelByIdError(ctx: StateContext<ChannelStateModel>, action: LoadChannelByIdError) {
        ctx.patchState({ loading: false, error: action.error });
    }

    @Action(AssignAgent)
    assignAgent(ctx: StateContext<ChannelStateModel>, action: AssignAgent) {
        return this.chatService.assignAgent(action.channelId, action.agentId).pipe(
            tap((channel) => {
                ctx.dispatch(new UpdateChannel(channel));
            }),
            catchError((error) => {
                ctx.patchState({ error });
                return of(error);
            }),
        );
    }

    @Action(LoadChannelsByType, { cancelUncompleted: true })
    loadChannelsByType(ctx: StateContext<ChannelStateModel>, action: LoadChannelsByType) {
        ctx.patchState({ loading: true, error: null });
        const wantedLimit = action.limit ?? 50;
        let accumulated = 0;

        return this.chatService
            .getChannelsByType(action.types, action.limit, action.nextToken, action.sortBy, action.sortOrder)
            .pipe(
                expand((conn) => {
                    accumulated += conn.items.length;
                    if (conn.nextToken && accumulated < wantedLimit) {
                        return this.chatService.getChannelsByType(
                            action.types, action.limit, conn.nextToken, action.sortBy, action.sortOrder,
                        );
                    }
                    return EMPTY;
                }),
                reduce<ChannelConnection, { items: Channel[]; nextToken?: string }>((acc, conn) => ({
                    items: [...acc.items, ...conn.items],
                    nextToken: conn.nextToken,
                }), { items: [], nextToken: undefined }),
                tap((merged) => {
                    ctx.dispatch(new LoadChannelsByTypeSuccess(merged, action.nextToken));
                }),
                catchError((error) => {
                    ctx.patchState({ loading: false, error });
                    return of(error);
                }),
            );
    }

    @Action(LoadChannelsByTypeSuccess)
    loadChannelsByTypeSuccess(
        ctx: StateContext<ChannelStateModel>,
        action: LoadChannelsByTypeSuccess,
    ) {
        const state = ctx.getState();
        const shouldAppend = !!action.requestNextToken;
        const existing = shouldAppend ? state.filteredChannels : [];
        const existingIds = new Set(existing.map((c) => c._id));
        const newItems = action.channelConnection.items.filter((c) => !existingIds.has(c._id));
        ctx.patchState({
            filteredChannels: [...existing, ...newItems],
            filteredNextToken: action.channelConnection.nextToken,
            loading: false,
            error: null,
            lastUpdated: Date.now(),
        });
    }

    @Action(ClearFilteredChannels)
    clearFilteredChannels(ctx: StateContext<ChannelStateModel>) {
        ctx.patchState({ filteredChannels: [], filteredNextToken: undefined });
    }

    @Action(LoadChannelStats)
    loadChannelStats(ctx: StateContext<ChannelStateModel>, action: LoadChannelStats) {
        return this.chatService.getChannelStats(action.statuses, action.serviceTypes).pipe(
            tap((stats) => ctx.dispatch(new LoadChannelStatsSuccess(stats))),
            catchError((error) => {
                console.error('Error loading channel stats:', error);
                return of(error);
            }),
        );
    }

    @Action(LoadChannelStatsSuccess)
    loadChannelStatsSuccess(ctx: StateContext<ChannelStateModel>, action: LoadChannelStatsSuccess) {
        ctx.patchState({ stats: action.stats });
    }

    @Action(LoadGlobalChannelStats)
    loadGlobalChannelStats(ctx: StateContext<ChannelStateModel>, action: LoadGlobalChannelStats) {
        return this.chatService.getChannelStats(undefined, action.serviceTypes).pipe(
            tap((stats) => ctx.dispatch(new LoadGlobalChannelStatsSuccess(stats))),
            catchError((error) => {
                console.error('Error loading global channel stats:', error);
                return of(error);
            }),
        );
    }

    @Action(LoadGlobalChannelStatsSuccess)
    loadGlobalChannelStatsSuccess(
        ctx: StateContext<ChannelStateModel>,
        action: LoadGlobalChannelStatsSuccess,
    ) {
        ctx.patchState({ globalStats: action.stats });
    }

    @Action(SetSelectedChannel)
    setSelectedChannel(ctx: StateContext<ChannelStateModel>, action: SetSelectedChannel) {
        if (!action.channelId) {
            // Navigating away from chat — clear selected channel so incoming
            // messages are not incorrectly marked as read in the background.
            ctx.patchState({ selectedChannelId: undefined });
            return;
        }
        ctx.patchState({ selectedChannelId: action.channelId });
        ctx.dispatch(new LoadChannelById(action.channelId));
    }
}
